import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import {
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";

import { useAvailableRoles } from "../../hooks/useAdminUsers";

import AdminPage from "./AdminPage";

const RolesPage: React.FC = () => {
  const { t } = useTranslation();
  // React Query hook
  const {
    data: rolesData,
    isLoading: loading,
    error: rolesError,
  } = useAvailableRoles();

  // Extract data from React Query response
  const roles = rolesData?.data || [];
  const error = rolesError ? t("admin.roles.loadError") : "";

  const getStatusColor = (isActive: boolean) => {
    return isActive ? "success" : "error";
  };

  return (
    <AdminPage>
      <Box>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h4">{t("admin.roles.title")}</Typography>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: 2,
                boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
              }}
            >
              <CardContent sx={{ pb: 2 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255, 255, 255, 0.8)",
                        fontWeight: 500,
                        mb: 1,
                      }}
                    >
                      {t("admin.roles.totalRoles")}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                      }}
                    >
                      {roles.length}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255, 255, 255, 0.9)" }}
                    >
                      {t("admin.roles.systemRoles")}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SecurityIcon sx={{ fontSize: 32 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                color: "white",
                borderRadius: 2,
                boxShadow: "0 8px 32px rgba(17, 153, 142, 0.3)",
              }}
            >
              <CardContent sx={{ pb: 2 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255, 255, 255, 0.8)",
                        fontWeight: 500,
                        mb: 1,
                      }}
                    >
                      {t("admin.roles.activeRoles")}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                      }}
                    >
                      {roles.filter((role) => role.isActive).length}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255, 255, 255, 0.9)" }}
                    >
                      {t("admin.roles.currentlyActive")}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 32 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Roles Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("admin.roles.columnRoleName")}</TableCell>
                  <TableCell>{t("admin.roles.columnDescription")}</TableCell>
                  <TableCell>{t("admin.roles.columnStatus")}</TableCell>
                  <TableCell>{t("admin.roles.columnRoleId")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : roles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {t("admin.roles.noRolesFound")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  roles.map((role) => (
                    <TableRow key={role.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <SecurityIcon color="primary" fontSize="small" />
                          <Typography variant="body1" fontWeight="medium">
                            {role.name}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {role.description || t("admin.roles.noDescription")}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={role.isActive ? t("admin.active") : t("admin.inactive")}
                          size="small"
                          color={
                            getStatusColor(role.isActive) as "success" | "error"
                          }
                          icon={
                            role.isActive ? <CheckCircleIcon /> : <CancelIcon />
                          }
                        />
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            backgroundColor: "#f5f5f5",
                            padding: "4px 8px",
                            borderRadius: 1,
                            fontSize: "0.75rem",
                          }}
                        >
                          {role.id}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </AdminPage>
  );
};

export default RolesPage;
